import _ from 'underscore';
import { toRuleDescription } from '@bingads-webui-campaign/bulk-operation-base';
import { legacyDeliveryStatus } from '@bingads-webui/filter-entity';
import { fromFilterEntityToLegacyFilter } from '@bingads-webui-campaign/filter-converter';
import { scopeOptions, entityRelatedOptions } from './options';
import * as gridHelper from './grid-helper';

function replaceAtom(atom, oldKey, newKey) {
  if (oldKey in atom) {
    const newAtom = {
      ...atom,
      [newKey]: atom[oldKey],
    };
    delete newAtom[oldKey];
    return newAtom;
  }
  return atom;
}

function replaceKeyOfQuery(query, oldKey, newKey) {
  if (!query) return query;
  let finalQuery = { ...query };
  if (oldKey in finalQuery) {
    finalQuery = replaceAtom(finalQuery, oldKey, newKey);
  } else if ('$and' in finalQuery) {
    finalQuery.$and.forEach((value, index, array) => {
      // eslint-disable-next-line no-param-reassign
      array[index] = replaceAtom(value, oldKey, newKey);
    });
  }
  return finalQuery;
}

export class RuleAuthoringHelper {
  constructor({
    i18n,
    permissions,
  }) {
    this.i18n = i18n;
    this.permissions = permissions;
  }

  setSelectionAndDescription(view, model, grid, isSimulation) {
    const filter = this.getCurrentFilter(view, model);
    let selectAll = false;
    let selectedCounts = 0;

    // only need to know selectall & selectedCounts in idbased senario
    // when filter[0].values is empty
    if (filter.idBased &&
      filter.FilterExpressions &&
      filter.FilterExpressions[0] &&
      _.isEmpty(filter.FilterExpressions[0].Values)) {
      selectAll = gridHelper.isCrossPageAllSelected(grid);
      selectedCounts = gridHelper.getSelectedRowsCount(grid);
    }

    model.set('Selection', this.getCurrentSelection(view, model, grid, isSimulation, filter));
    model.set('Description', toRuleDescription(filter, selectAll, selectedCounts));
  }

  getCurrentSelection(view, model, grid, isSimulation, currentFilter) {
    let query = '';
    const { filterEntity, selectionRangeFilterQuery } = currentFilter;

    const selection = {
      CampaignId: view.viewModel.selectedScopeInfo.campaignId,
      AdGroupId: view.viewModel.selectedScopeInfo.adGroupId,
    };

    if (isSimulation) {
      const { pageSize, pageNumber } = grid.pgridPlugins.pagination;
      const sortInfo = gridHelper.getSortInfo(grid);
      let sortStr = '';

      if (sortInfo && !view.viewModel.isEditRule) {
        sortStr = `&$orderby=${sortInfo}`;
      }
      query = `$top=${pageSize}&$skip=${pageNumber * pageSize}${sortStr}`;
      query += `&daterangepreset=${view.cronselectionView.getSelectedDataDateRangeOption()}`;
    } else {
      selection.DateRange = {
        DateRangePreset:
          view.cronselectionView.getSelectedDataDateRangeOption(),
      };
    }

    let filterQueryUrl = filterEntity ? filterEntity.odataV4() : null;
    // merge filter url from user filter selection with range selection
    if (filterQueryUrl && selectionRangeFilterQuery) {
      filterQueryUrl += ` and (${selectionRangeFilterQuery})`;
    } else if (selectionRangeFilterQuery) {
      filterQueryUrl = selectionRangeFilterQuery;
    }
    const filterEncodedUrl = filterQueryUrl ? encodeURIComponent(filterQueryUrl) : null;
    const filterURLParam = filterEncodedUrl ? `$filter=(${filterEncodedUrl})` : '';

    if (filterURLParam) {
      query = query === '' ? filterURLParam : `${query}&${filterURLParam}`;
    }

    selection.QueryString = query;

    return selection;
  }

  getCurrentFilter(view, model) {
    const entity = model.get('Entity');
    const { viewModel, predicateListView } = view;
    let filters = null;
    let idBased = false;
    let selectionRangeFilterQuery = null;

    if (viewModel.selectedScopeOption() === scopeOptions.Selected) {
      idBased = true;
      const ids = viewModel.selectedScopeInfo.selectedRowIds;
      filters = {
        FilterExpressions: [{
          SelectedColumn: `${entity}Id`, // 'CampaignId', 'AdgroupId'.
          SelectedOperator: 'Equal',
          Values: ids,
        }],
      };

      if (ids && ids.length > 0) {
        selectionRangeFilterQuery = _.map(ids, id => `Id eq ${id}`).join(' or ');
      }
    } else if (viewModel.selectedScopeOption() === scopeOptions.AllEnabled) {
      filters = {
        FilterExpressions: [{
          SelectedColumn: entityRelatedOptions.EnabledStatusFilterColumn[entity],
          SelectedOperator: 'EnumSelection',
          Values: entityRelatedOptions.EnabledStatusFilterValue[entity],
        }],
      };
      selectionRangeFilterQuery = entityRelatedOptions.EnabledStatusFilterQueryOdata[entity];
    }

    const selectedFilter = {};
    const predicateListFilter = view.isForVNext ?
      predicateListView.editableFilter : predicateListView.getFilterEntity();

    // ToDO: apjia,remove this after the automated rules tab support new filter
    // (currently we need use same format for that tab)
    selectedFilter.FilterExpressions =
      fromFilterEntityToLegacyFilter(predicateListFilter, this.i18n);

    let expr = null;
    if (selectedFilter && selectedFilter.FilterExpressions) {
      expr = _.map(selectedFilter.FilterExpressions, (item) => {
        const mappedResult = {
          SelectedColumn: item.SelectedColumn,
          SelectedOperator: item.SelectedOperator,
          Values: item.Values,
        };

        if (!_.isEmpty(item.SelectedNames)) {
          mappedResult.SelectedNames = item.SelectedNames;
        }
        return mappedResult;
      });

      // LSR[1392134]: if user set filter "DeliveryStasus contains Eligible" in campaign grid
      //  actually it's mapped to eligible and inlineOpportunity, we need pass it to MT
      if (entity === 'Campaign') {
        _.each(expr, (filter) => {
          if (filter.SelectedColumn === 'DeliveryStatus' &&
            _.contains(filter.Values, legacyDeliveryStatus.Eligible.toString()) &&
            !_.contains(filter.Values, legacyDeliveryStatus.InlineOpportunity.toString())) {
            filter.Values.push(legacyDeliveryStatus.InlineOpportunity.toString());
          }
        });
      }
    }
    if (filters && filters.FilterExpressions) {
      filters.FilterExpressions = _.union(filters.FilterExpressions, expr);
    } else {
      filters = {
        FilterExpressions: expr,
      };
    }

    // 'DataExclusionId' is an alias of 'SeasonalityAdjustmentId'
    // Convert it before sending request to make back-end happy.
    if (predicateListFilter && predicateListFilter.query) {
      const finalQuery = replaceKeyOfQuery(predicateListFilter.query, 'DataExclusionId', 'SeasonalityAdjustmentId');
      predicateListFilter.query = finalQuery;
    }

    return {
      FilterExpressions: (filters && filters.FilterExpressions) || [],
      filterEntity: predicateListFilter,
      idBased,
      selectionRangeFilterQuery,
    };
  }
}
